import { preventPopoverCloseOnSpacebarPress, stopPropagation } from '@/_shared/tools/event-prevention.js';
import { Button, Input } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import type { Column } from '@tanstack/react-table';
import { useCallback, useRef } from 'react';
import { resetFilter } from '../../_shared/reset.js';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetNumberColumnFilterValue } from '../filter/index.js';
import { NUMBER_FILTER_OPERATION, type NumberFilterValuesWithoutInArray } from '../filter/operation.js';
import type { NumberFilterSchemaWithoutInArray } from '../filter/schema.js';
import { useStyles } from './styles.js';

type NumberFilterProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: NumberFilterValuesWithoutInArray['id'];
};

export function NumberFilter<TData, TValue>({ column, dropdownValue }: NumberFilterProps<TData, TValue>) {
    const styles = useStyles();
    const sharedStyles = useSharedStyles();

    const filter = column.getFilterValue() as NumberFilterSchemaWithoutInArray | undefined;
    const value1 = filter && 'value1' in filter ? filter.value1 : undefined;
    const value2 = filter && 'value2' in filter ? filter.value2 : undefined;

    const inputValue1Ref = useRef<HTMLInputElement>(null);
    const inputValue2Ref = useRef<HTMLInputElement>(null);

    const clearValue1Input = useCallback(() => {
        typedSetNumberColumnFilterValue(column, {
            ...(column.getFilterValue() as NumberFilterSchemaWithoutInArray),
            operation: dropdownValue,
            value1: undefined,
        });

        inputValue1Ref.current?.focus();
    }, [column, dropdownValue]);

    const clearValue2Input = useCallback(() => {
        typedSetNumberColumnFilterValue(column, {
            ...(column.getFilterValue() as NumberFilterSchemaWithoutInArray),
            operation: dropdownValue,
            value2: undefined,
        });

        inputValue2Ref.current?.focus();
    }, [column, dropdownValue]);

    const onValue1Change = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            const parsedValue: number | undefined = value.length ? parseInt(value) : undefined;

            if (parsedValue === undefined && value2 === undefined) {
                resetFilter(column);
            } else {
                typedSetNumberColumnFilterValue(column, {
                    ...(column.getFilterValue() as NumberFilterSchemaWithoutInArray),
                    operation: dropdownValue,
                    value1: parsedValue,
                });
            }
        },
        [column, dropdownValue, value2]
    );

    const onValue2Change = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const _value = event.target.value;
            const value: number | undefined = _value.length ? parseInt(_value) : undefined;

            if (value === undefined && value1 === undefined) {
                resetFilter(column);
            } else {
                typedSetNumberColumnFilterValue(column, {
                    ...(column.getFilterValue() as NumberFilterSchemaWithoutInArray),
                    operation: dropdownValue,
                    value2: value,
                });
            }
        },
        [column, dropdownValue, value1]
    );

    return (
        <div className={styles.container}>
            <Input
                ref={inputValue1Ref}
                className={sharedStyles.input}
                placeholder={dropdownValue === NUMBER_FILTER_OPERATION.between.id ? 'Von' : 'Wert'}
                type="number"
                min={0}
                value={typeof value1 === 'number' ? value1.toString() : ''}
                onChange={onValue1Change}
                onMouseDown={stopPropagation}
                onKeyDown={preventPopoverCloseOnSpacebarPress}
                contentAfter={
                    <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={clearValue1Input} />
                }
            />

            {dropdownValue === NUMBER_FILTER_OPERATION.between.id && (
                <Input
                    ref={inputValue2Ref}
                    className={sharedStyles.input}
                    placeholder="Bis"
                    type="number"
                    min={inputValue1Ref.current?.value ? parseInt(inputValue1Ref.current.value) + 1 : 0}
                    value={typeof value2 === 'number' ? value2.toString() : ''}
                    onChange={onValue2Change}
                    onMouseDown={stopPropagation}
                    onKeyDown={preventPopoverCloseOnSpacebarPress}
                    contentAfter={
                        <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={clearValue2Input} />
                    }
                />
            )}
        </div>
    );
}
